import { useState, useEffect } from 'react';
import { Slider } from '@/components/ui/slider';
import { HexColorPicker } from 'react-colorful';

interface MaterialInputProps {
  /**
   * 22-element array:
   * [metalness, roughness, emissive_r, emissive_g, emissive_b, emissive_intensity,
   *  clearcoat, clearcoat_roughness,
   *  sheen, sheen_roughness, sheen_color_r, sheen_color_g, sheen_color_b,
   *  iridescence, iridescence_ior,
   *  transmission, ior, thickness,
   *  attenuation_color_r, attenuation_color_g, attenuation_color_b, attenuation_distance]
   */
  value: number[];
  onChange: (value: number[]) => void;
}

function floatToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.round(Math.max(0, Math.min(255, n * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

function hexToFloats(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  const fmt = (n: number) => parseFloat(n.toFixed(3));
  return [fmt(r), fmt(g), fmt(b)];
}

/** Check if any physical-only property is set (not -1) */
function hasPhysicalProps(vals: number[]): boolean {
  for (let i = 6; i < 22; i++) {
    if (vals[i] !== undefined && vals[i] >= 0) return true;
  }
  return false;
}

/**
 * Renders a mini slider-style icon: a track with a filled region from left
 * up to `t` (0–1), topped by a horizontal line. For color props the fill
 * uses the property color.
 */
function SliderIcon({
  t,
  color,
  active,
  isColor = false,
}: {
  t: number;
  color?: string;
  active: boolean;
  isColor?: boolean;
}) {
  const tc = Math.max(0, Math.min(1, t));
  const W = 28;
  const H = 9;
  const pad = 2;
  const trackY = H - 2;
  const trackH = 3; // height of the filled area below the line
  const x1 = pad;
  const x2 = W - pad;
  const fillX2 = x1 + tc * (x2 - x1);
  const lineColor = isColor && color ? color : 'currentColor';
  const lineOpacity = active ? 1 : 0.5;
  const fillOpacity = active ? 0.18 : 0.08;

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      fill="none"
      className="pointer-events-none"
      style={{ display: 'block' }}
    >
      {/* Filled region below the value line */}
      {tc > 0 && (
        <rect
          x={x1}
          y={trackY - trackH + 1}
          width={fillX2 - x1}
          height={trackH}
          rx={1}
          fill={isColor && color ? color : 'currentColor'}
          opacity={fillOpacity}
        />
      )}
      {/* Full track background line */}
      <line
        x1={x1}
        y1={trackY}
        x2={x2}
        y2={trackY}
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        opacity={active ? 0.2 : 0.12}
      />
      {/* Value line — positioned at t along the track */}
      <line
        x1={x1}
        y1={trackY}
        x2={fillX2}
        y2={trackY}
        stroke={lineColor}
        strokeWidth={active ? 1.5 : 1}
        strokeLinecap="round"
        opacity={lineOpacity}
      />
    </svg>
  );
}

/** A single icon button in the icon bar */
function PropertyIconButton({
  label,
  t,
  color,
  active,
  onClick,
  isColor = false,
  dimmed = false,
}: {
  label: string;
  t: number;
  color?: string;
  active: boolean;
  onClick: () => void;
  isColor?: boolean;
  dimmed?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label}: ${isColor ? (color ?? 'none') : t.toFixed(2)}`}
      className={[
        'flex flex-col items-center gap-px rounded px-1 py-0.5 transition-all',
        active
          ? 'bg-white/10 text-foreground ring-1 ring-white/20'
          : 'text-muted-foreground hover:bg-white/5 hover:text-foreground',
        dimmed && !active ? 'opacity-40' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <SliderIcon t={t} color={color} active={active} isColor={isColor} />
      <span
        className="max-w-[36px] truncate text-center leading-none"
        style={{ fontSize: '8px' }}
      >
        {label}
      </span>
    </button>
  );
}

/** Expanded slider row shown below the icon bar */
function ExpandedSlider({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-md bg-muted/40 px-2 py-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs tabular-nums text-foreground">
          {value.toFixed(2)}
        </span>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={min}
        max={max}
        step={step}
        className="h-6"
      />
    </div>
  );
}

/** Expanded color picker shown below the icon bar */
function ExpandedColorPicker({
  label,
  hex,
  onChange,
}: {
  label: string;
  hex: string;
  onChange: (hex: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-md bg-muted/40 px-2 py-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1.5">
          <div
            className="h-3 w-3 rounded-full shadow-sm ring-1 ring-white/20"
            style={{ backgroundColor: hex }}
          />
          <span className="font-mono text-[10px] uppercase text-muted-foreground">
            {hex}
          </span>
        </div>
      </div>
      <HexColorPicker
        color={hex}
        onChange={onChange}
        style={{ width: '100%', height: '120px' }}
      />
    </div>
  );
}

// ─── Property definitions ────────────────────────────────────────────────────

type PropKey =
  | 'metalness'
  | 'roughness'
  | 'emissive'
  | 'emissiveIntensity'
  | 'clearcoat'
  | 'clearcoatRoughness'
  | 'sheen'
  | 'sheenRoughness'
  | 'sheenColor'
  | 'iridescence'
  | 'iridescenceIOR'
  | 'transmission'
  | 'ior'
  | 'thickness'
  | 'attenuationColor'
  | 'attenuationDistance';

const STANDARD_PROPS: PropKey[] = [
  'metalness',
  'roughness',
  'emissive',
  'emissiveIntensity',
];

const PHYSICAL_PROPS: PropKey[] = [
  'clearcoat',
  'clearcoatRoughness',
  'sheen',
  'sheenRoughness',
  'sheenColor',
  'iridescence',
  'iridescenceIOR',
  'transmission',
  'ior',
  'thickness',
  'attenuationColor',
  'attenuationDistance',
];

const PROP_LABELS: Record<PropKey, string> = {
  metalness: 'Metal',
  roughness: 'Rough',
  emissive: 'Emit',
  emissiveIntensity: 'E.Int',
  clearcoat: 'Coat',
  clearcoatRoughness: 'C.Rgh',
  sheen: 'Sheen',
  sheenRoughness: 'S.Rgh',
  sheenColor: 'S.Col',
  iridescence: 'Irid',
  iridescenceIOR: 'I.IOR',
  transmission: 'Trans',
  ior: 'IOR',
  thickness: 'Thick',
  attenuationColor: 'A.Col',
  attenuationDistance: 'A.Dst',
};

const COLOR_PROPS = new Set<PropKey>([
  'emissive',
  'sheenColor',
  'attenuationColor',
]);

// ─── Main component ──────────────────────────────────────────────────────────

export function MaterialInput({ value, onChange }: MaterialInputProps) {
  // Ensure we have a full 22-element array (pad with -1 for backwards compat)
  const padded = [...value, ...Array(22).fill(-1)].slice(0, 22);
  const [vals, setVals] = useState(padded);
  // Set of open property keys
  const [openProps, setOpenProps] = useState<Set<PropKey>>(new Set());

  useEffect(() => {
    const p = [...value, ...Array(22).fill(-1)].slice(0, 22);
    setVals(p);
  }, [value]);

  const update = (index: number, v: number) => {
    const next = [...vals];
    next[index] = parseFloat(v.toFixed(4));
    setVals(next);
    onChange(next);
  };

  const updateColor = (startIdx: number, hex: string) => {
    const [r, g, b] = hexToFloats(hex);
    const next = [...vals];
    next[startIdx] = r;
    next[startIdx + 1] = g;
    next[startIdx + 2] = b;
    setVals(next);
    onChange(next);
  };

  const toggleProp = (key: PropKey) => {
    setOpenProps((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // ── Derived values ──
  const metalness = vals[0] >= 0 ? vals[0] : 0;
  const roughness = vals[1] >= 0 ? vals[1] : 0.5;
  const hasEmissive = vals[2] >= 0 || vals[3] >= 0 || vals[4] >= 0;
  const emissiveR = vals[2] >= 0 ? vals[2] : 0;
  const emissiveG = vals[3] >= 0 ? vals[3] : 0;
  const emissiveB = vals[4] >= 0 ? vals[4] : 0;
  const emissiveHex = floatToHex(emissiveR, emissiveG, emissiveB);
  const emissiveIntensity = vals[5] >= 0 ? vals[5] : 0;

  const showPhysical = hasPhysicalProps(vals);
  const clearcoat = vals[6] >= 0 ? vals[6] : 0;
  const clearcoatRoughness = vals[7] >= 0 ? vals[7] : 0;
  const sheen = vals[8] >= 0 ? vals[8] : 0;
  const sheenRoughness = vals[9] >= 0 ? vals[9] : 0.5;
  const hasSheenColor = vals[10] >= 0 || vals[11] >= 0 || vals[12] >= 0;
  const sheenColorR = vals[10] >= 0 ? vals[10] : 1;
  const sheenColorG = vals[11] >= 0 ? vals[11] : 1;
  const sheenColorB = vals[12] >= 0 ? vals[12] : 1;
  const sheenColorHex = floatToHex(sheenColorR, sheenColorG, sheenColorB);
  const iridescence = vals[13] >= 0 ? vals[13] : 0;
  const iridescenceIOR = vals[14] >= 0 ? vals[14] : 1.3;
  const transmission = vals[15] >= 0 ? vals[15] : 0;
  const ior = vals[16] >= 0 ? vals[16] : 1.5;
  const thickness = vals[17] >= 0 ? vals[17] : 0;
  const hasAttColor = vals[18] >= 0 || vals[19] >= 0 || vals[20] >= 0;
  const attColorR = vals[18] >= 0 ? vals[18] : 1;
  const attColorG = vals[19] >= 0 ? vals[19] : 1;
  const attColorB = vals[20] >= 0 ? vals[20] : 1;
  const attColorHex = floatToHex(attColorR, attColorG, attColorB);
  const attDistance = vals[21] >= 0 ? vals[21] : 0;

  // Map each PropKey → normalized t value (0–1) and color for icon rendering
  const propT: Record<PropKey, number> = {
    metalness,
    roughness,
    emissive: hasEmissive ? 1 : 0,
    emissiveIntensity: Math.min(emissiveIntensity / 10, 1),
    clearcoat,
    clearcoatRoughness,
    sheen,
    sheenRoughness,
    sheenColor: hasSheenColor ? 1 : 0,
    iridescence,
    iridescenceIOR: (iridescenceIOR - 1) / (2.333 - 1),
    transmission,
    ior: (ior - 1) / (2.333 - 1),
    thickness: Math.min(thickness / 10, 1),
    attenuationColor: hasAttColor ? 1 : 0,
    attenuationDistance: Math.min(attDistance / 100, 1),
  };

  const propColor: Partial<Record<PropKey, string>> = {
    emissive: hasEmissive ? emissiveHex : undefined,
    sheenColor: hasSheenColor ? sheenColorHex : undefined,
    attenuationColor: hasAttColor ? attColorHex : undefined,
  };

  // ── Render an expanded panel for a given key ──
  const renderExpanded = (key: PropKey) => {
    if (!openProps.has(key)) return null;

    switch (key) {
      case 'metalness':
        return (
          <ExpandedSlider
            key={key}
            label="Metalness"
            value={metalness}
            onChange={(v) => update(0, v)}
          />
        );
      case 'roughness':
        return (
          <ExpandedSlider
            key={key}
            label="Roughness"
            value={roughness}
            onChange={(v) => update(1, v)}
          />
        );
      case 'emissive':
        return (
          <ExpandedColorPicker
            key={key}
            label="Emissive Color"
            hex={emissiveHex}
            onChange={(c) => updateColor(2, c)}
          />
        );
      case 'emissiveIntensity':
        return (
          <ExpandedSlider
            key={key}
            label="Emissive Intensity"
            value={emissiveIntensity}
            onChange={(v) => update(5, v)}
            max={10}
            step={0.1}
          />
        );
      case 'clearcoat':
        return (
          <ExpandedSlider
            key={key}
            label="Clearcoat"
            value={clearcoat}
            onChange={(v) => update(6, v)}
          />
        );
      case 'clearcoatRoughness':
        return (
          <ExpandedSlider
            key={key}
            label="Clearcoat Roughness"
            value={clearcoatRoughness}
            onChange={(v) => update(7, v)}
          />
        );
      case 'sheen':
        return (
          <ExpandedSlider
            key={key}
            label="Sheen"
            value={sheen}
            onChange={(v) => update(8, v)}
          />
        );
      case 'sheenRoughness':
        return (
          <ExpandedSlider
            key={key}
            label="Sheen Roughness"
            value={sheenRoughness}
            onChange={(v) => update(9, v)}
          />
        );
      case 'sheenColor':
        return (
          <ExpandedColorPicker
            key={key}
            label="Sheen Color"
            hex={sheenColorHex}
            onChange={(c) => updateColor(10, c)}
          />
        );
      case 'iridescence':
        return (
          <ExpandedSlider
            key={key}
            label="Iridescence"
            value={iridescence}
            onChange={(v) => update(13, v)}
          />
        );
      case 'iridescenceIOR':
        return (
          <ExpandedSlider
            key={key}
            label="Iridescence IOR"
            value={iridescenceIOR}
            onChange={(v) => update(14, v)}
            min={1}
            max={2.333}
            step={0.01}
          />
        );
      case 'transmission':
        return (
          <ExpandedSlider
            key={key}
            label="Transmission"
            value={transmission}
            onChange={(v) => update(15, v)}
          />
        );
      case 'ior':
        return (
          <ExpandedSlider
            key={key}
            label="IOR"
            value={ior}
            onChange={(v) => update(16, v)}
            min={1}
            max={2.333}
            step={0.01}
          />
        );
      case 'thickness':
        return (
          <ExpandedSlider
            key={key}
            label="Thickness"
            value={thickness}
            onChange={(v) => update(17, v)}
            max={10}
            step={0.1}
          />
        );
      case 'attenuationColor':
        return (
          <ExpandedColorPicker
            key={key}
            label="Attenuation Color"
            hex={attColorHex}
            onChange={(c) => updateColor(18, c)}
          />
        );
      case 'attenuationDistance':
        return (
          <ExpandedSlider
            key={key}
            label="Attenuation Distance"
            value={attDistance}
            onChange={(v) => update(21, v)}
            max={100}
            step={0.5}
          />
        );
      default:
        return null;
    }
  };

  // ── Icon bar row ──
  const renderRow = (keys: PropKey[], dimmed: boolean) => (
    <div className="flex flex-wrap gap-0.5">
      {keys.map((key) => (
        <PropertyIconButton
          key={key}
          label={PROP_LABELS[key]}
          t={propT[key]}
          color={propColor[key]}
          active={openProps.has(key)}
          onClick={() => toggleProp(key)}
          isColor={COLOR_PROPS.has(key)}
          dimmed={dimmed}
        />
      ))}
    </div>
  );

  // Expanded panels: render in a fixed order (standard first, then physical)
  const allKeysInOrder: PropKey[] = [...STANDARD_PROPS, ...PHYSICAL_PROPS];

  return (
    <div className="flex flex-col gap-2">
      {/* Icon bar */}
      <div className="flex flex-col gap-1 rounded-md bg-muted/30 px-2 py-2">
        {/* Standard row */}
        {renderRow(STANDARD_PROPS, false)}
        {/* Physical row — always shown, but dimmed if no physical props active */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <div className="h-px flex-1 bg-border/30" />
            <span
              className={[
                'text-[9px] uppercase tracking-wider',
                showPhysical
                  ? 'text-muted-foreground/60'
                  : 'text-muted-foreground/30',
              ].join(' ')}
            >
              Physical
            </span>
            <div className="h-px flex-1 bg-border/30" />
          </div>
          {renderRow(PHYSICAL_PROPS, !showPhysical)}
        </div>
      </div>

      {/* Expanded panels */}
      {allKeysInOrder.map((key) => renderExpanded(key))}
    </div>
  );
}
